import { FC, useState } from 'react';
import { createUseStyles } from 'react-jss';
import { AgencyAlertKind, IWeeklyPoint, monthDay } from '@innerhive-internal/innerhive-common';
import {
    BRAND,
    CHART_INK,
    KIND_COLOR,
    KIND_DESCRIPTION,
    KIND_LABEL,
    KIND_ORDER,
    KIND_SENTENCE_LABEL,
    KIND_TINT
} from './agencyChartTheme';

const HEIGHT = 168;
const TOP_PAD = 10;
const AXIS_HEIGHT = 20;
const SEGMENT_GAP = 2;
const COLUMN_GAP = 6;

/** How far a bar recedes when the reader has picked a different kind from the legend. */
const RECEDED_OPACITY = 0.3;

const useStyles = createUseStyles({
    segment: {
        transition: 'fill 120ms ease, fill-opacity 120ms ease'
    },

    caption: {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 12,
        minHeight: 20,
        marginBottom: 6,
        fontSize: 12.5,
        color: CHART_INK.label
    },

    captionStrong: {
        color: BRAND.deepTeal,
        fontWeight: 600
    },

    svg: {
        display: 'block',
        width: '100%',
        height: 'auto'
    },

    column: {
        cursor: 'pointer',

        '&:focus-visible': {
            outline: 'none'
        },
        '&:focus-visible .ih-week-target': {
            stroke: BRAND.deepTeal,
            strokeWidth: 2
        }
    },

    axisLabel: {
        fontSize: 11,
        fill: CHART_INK.label
    },

    axisLabelOn: {
        fontSize: 11,
        fontWeight: 600,
        fill: BRAND.deepTeal
    },

    legend: {
        display: 'flex',
        flexWrap: 'wrap' as const,
        gap: '6px 10px',
        marginTop: 12
    },

    legendItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 7,
        padding: '4px 10px 4px 8px',
        borderRadius: 20,
        border: `1px solid ${BRAND.tealEdge}`,
        background: 'none',
        font: 'inherit',
        fontSize: 12.5,
        color: CHART_INK.label,
        cursor: 'pointer',

        '&:hover': {
            background: BRAND.tealWash
        }
    },

    legendItemOn: {
        background: BRAND.deepTeal,
        borderColor: BRAND.deepTeal,
        color: '#fff',
        fontWeight: 500,

        '&:hover': {
            background: BRAND.deepTeal
        }
    },

    swatch: {
        width: 11,
        height: 11,
        borderRadius: 3,
        borderStyle: 'solid',
        borderWidth: 1.5,
        flexShrink: 0
    }
});

/**
 * Observations per week across the whole caseload, stacked by kind.
 *
 * Stacked rather than three lines because the two readers want different things from it and a stack
 * answers both: the column height is the load the agency is carrying, which is leadership's
 * question, and the composition is what kind of week it was, which is the dispatcher's. Overlapping
 * lines would answer neither well at this size.
 *
 * There is no second y-axis and there never should be. If a measure on a different scale needs to
 * appear beside this, it gets its own chart.
 *
 * A column OPENS that week's observations; only the kind in the legend narrows anything. Clicking a
 * column used to narrow the whole tab and it was the wrong model: a reader pressing a bar wants to
 * know what those observations were, and instead every figure on the page changed underneath them,
 * including on the other lens once they switched to it. Nothing is ever removed from the chart
 * itself either way, since a chart that deletes its own context cannot show whether a week was busy.
 */
export const CaseloadPulseChart: FC<{
    weekly: IWeeklyPoint[];
    selectedKind?: AgencyAlertKind;
    onOpenWeek: (weekStartsOn: string) => void;
    onSelectKind: (kind: AgencyAlertKind) => void;
}> = ({ weekly, selectedKind, onOpenWeek, onSelectKind }) => {
    const classes = useStyles();
    // Named for the column the reader is on rather than for the pointer, because focus puts them
    // there too: without that a keyboard user tabs the columns and gets no caption or date, and a
    // touch user has no hover at all.
    const [activeWeek, setActiveWeek] = useState<number | undefined>();

    if (weekly.length === 0) {
        return null;
    }

    const width = 640;
    const plotHeight = HEIGHT - AXIS_HEIGHT - TOP_PAD;
    const columnWidth = width / weekly.length;
    const barWidth = Math.max(6, columnWidth - COLUMN_GAP);

    const peak = Math.max(1, ...weekly.map(point => point.total));
    const scale = (value: number) => (value / peak) * plotHeight;

    // The caption follows whichever column the reader is on, and falls back to naming the busiest
    // week when they are on none.
    const reading = activeWeek === undefined ? undefined : weekly[activeWeek];
    const busiest = weekly.reduce((best, point) => (point.total > best.total ? point : best), weekly[0]);

    return (
        <div data-id="caseload-pulse-chart">
            <div className={classes.caption}>
                <span data-id="pulse-caption">
                    {reading
                        ? (
                            <>
                                <span className={classes.captionStrong}>Week of {monthDay(reading.weekStartsOn)}: </span>
                                {reading.total === 0
                                    ? 'nothing recorded'
                                    : KIND_ORDER.filter(kind => reading[kind] > 0)
                                        .map(kind => `${reading[kind]} ${KIND_SENTENCE_LABEL[kind]}`)
                                        .join(', ')}
                            </>
                        )
                        : `Busiest week was ${monthDay(busiest.weekStartsOn)}, with ${busiest.total} recorded.`}
                </span>
            </div>

            <svg
                className={classes.svg}
                viewBox={`0 0 ${width} ${HEIGHT}`}
                role="group"
                aria-label={`Observations per week across the caseload for the last ${weekly.length} weeks, by kind`}
                onMouseLeave={() => setActiveWeek(undefined)}
            >
                <line
                    x1={0}
                    y1={TOP_PAD + plotHeight}
                    x2={width}
                    y2={TOP_PAD + plotHeight}
                    stroke={CHART_INK.grid}
                    strokeWidth={1}
                />

                {weekly.map((point, index) => {
                    const x = index * columnWidth + (columnWidth - barWidth) / 2;
                    const isActive = activeWeek === index;
                    let cursorY = TOP_PAD + plotHeight;

                    return (
                        <g
                            key={point.weekStartsOn}
                            className={classes.column}
                            tabIndex={0}
                            role="button"
                            aria-label={`Week of ${monthDay(point.weekStartsOn)}, ${point.total} recorded`}
                            aria-haspopup="dialog"
                            onMouseEnter={() => setActiveWeek(index)}
                            onFocus={() => setActiveWeek(index)}
                            onBlur={() => setActiveWeek(undefined)}
                            onClick={event => {
                                // Ionic returns focus to the opener when the dialog closes, and the
                                // ring it draws then reads as a selection the reader cannot clear.
                                // Dropping focus on a pointer click leaves the keyboard path intact.
                                event.currentTarget.blur();
                                onOpenWeek(point.weekStartsOn);
                            }}
                            onKeyDown={event => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    onOpenWeek(point.weekStartsOn);
                                }
                            }}
                            data-id="pulse-column"
                            data-week={point.weekStartsOn}
                        >
                            {/* A full-height target so the thin columns, and the empty weeks, are
                                still reachable by a pointer. */}
                            <rect
                                className="ih-week-target"
                                x={index * columnWidth}
                                y={TOP_PAD}
                                width={columnWidth}
                                height={plotHeight}
                                rx={4}
                                fill="transparent"
                            />

                            {KIND_ORDER.map(kind => {
                                const value = point[kind];
                                if (value === 0) {
                                    return null;
                                }
                                // The gap goes between segments, not under the lowest one: the stack
                                // has to sit on the baseline or every column reads 2px short.
                                const segmentHeight = Math.max(2, scale(value) - SEGMENT_GAP);
                                cursorY -= segmentHeight;
                                const segmentY = cursorY;
                                cursorY -= SEGMENT_GAP;

                                // A lighter shade of the same colour at rest, filling in to full
                                // strength under the pointer. Full-strength bars across twelve weeks
                                // read as twelve alarms; the shape of the run is what this chart is
                                // for, and the week somebody is reading is the one worth the weight.
                                // Solid colours rather than one colour at low opacity, so a column
                                // never looks see-through against the card.
                                const isOtherKind = Boolean(selectedKind) && selectedKind !== kind;

                                return (
                                    <rect
                                        className={classes.segment}
                                        key={kind}
                                        x={x}
                                        y={segmentY}
                                        width={barWidth}
                                        height={segmentHeight}
                                        rx={3}
                                        fill={isActive ? KIND_COLOR[kind] : KIND_TINT[kind]}
                                        fillOpacity={isOtherKind ? RECEDED_OPACITY : 1}
                                    />
                                );
                            })}
                        </g>
                    );
                })}

                {weekly.map((point, index) => {
                    // Every third week is labelled: twelve labels at this width would collide, and the
                    // reader needs the shape of the run more than the date of each column. The active
                    // week is labelled whichever column it is, so the reader always has a date.
                    const isActive = activeWeek === index;
                    if (index % 3 !== 0 && !isActive) {
                        return null;
                    }
                    return (
                        <text
                            key={point.weekStartsOn}
                            className={isActive ? classes.axisLabelOn : classes.axisLabel}
                            x={index * columnWidth + columnWidth / 2}
                            y={HEIGHT - 5}
                            textAnchor="middle"
                        >
                            {monthDay(point.weekStartsOn)}
                        </text>
                    );
                })}
            </svg>

            <div className={classes.legend}>
                {KIND_ORDER.map(kind => (
                    <button
                        key={kind}
                        type="button"
                        className={`${classes.legendItem} ${selectedKind === kind ? classes.legendItemOn : ''}`}
                        data-id="pulse-legend-item"
                        data-kind={kind}
                        aria-pressed={selectedKind === kind}
                        title={KIND_DESCRIPTION[kind]}
                        onClick={() => onSelectKind(kind)}
                    >
                        {/* Filled as the bars rest and outlined as they read when active, so the key
                            matches the chart in both states. */}
                        <span
                            className={classes.swatch}
                            style={{ background: KIND_TINT[kind], borderColor: KIND_COLOR[kind] }}
                        />
                        {KIND_LABEL[kind]}
                    </button>
                ))}
            </div>
        </div>
    );
};
